import React, { useState, useEffect } from 'react';
import { ListTodo, Plus, Save } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import BaseDataTable, {
  ColumnType,
  IColumnConfig,
  IDataRow,
} from '../../../../components/ui/baseDataTable';
import { cn } from '../../../../lib/utils';
import { showSuccessToast, showErrorToast } from '../../../../lib/toast';
import ButtonV2 from '../../../../components/ui/button/v2';
import {
  selectPolicyImprovements,
  selectPolicyImprovementsStatus,
  selectPolicyImprovementsError,
} from '../../logic/PolicyImprovementsSlice';
import { PolicyImprovementItemUI } from '../../../../api/policyImprovements';
import {
  deletePolicyImprovementThunk,
  fetchPolicyImprovementsThunk,
  upsertPolicyImprovementsThunk,
} from '../../logic/PolicyImprovementsThunk';
import ClosureNoteModal from '../ActionAgainstInvolvedPartiesTable/ClosureNoteModal';
import EditableExpandableTextCell from './EditableExpandableTextCell';
import NoDataIcon from '../../../../assets/svg/EmptyTableIcon';
import { ErrorDisplay } from '../../../../components/ui/error/ErrorDisplay';
import { CustomLoadingSpinner } from '../../../../components/ui/spinners/LoadingSpinner';
import { RETRIEVING_DATA_SUBTITLE } from '../../../../lib/constants';
import TableContentWrapper from '../TableContentWrapper';

interface PolicyImprovementsTableV2Props {
  caseId: string;
}

const PolicyImprovementsTableV2: React.FC<PolicyImprovementsTableV2Props> = ({
  caseId,
}) => {
  const dispatch = useAppDispatch();
  const savedItems = useAppSelector(selectPolicyImprovements);
  const fetchStatus = useAppSelector(selectPolicyImprovementsStatus);
  const fetchError = useAppSelector(selectPolicyImprovementsError);

  const [localItems, setLocalItems] = useState<PolicyImprovementItemUI[] | null>(null);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [tableKey, setTableKey] = useState(0);

  const [isClosureNoteModalOpen, setIsClosureNoteModalOpen] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [modalVariant, setModalVariant] = useState<'edit' | 'view'>('edit');

  useEffect(() => {
    if (caseId) {
      dispatch(fetchPolicyImprovementsThunk({ caseId }));
    }
  }, [caseId, dispatch]);

  const dataItems = localItems ?? savedItems;
  const cloneFromSaved = () => savedItems.map((item) => ({ ...item }));

  const hasEmptyNewRow = dataItems.some(
    (r) => r.is_added && r.policy_change.trim() === ''
  );

 const isDirty = dataItems.some(
    (r) => r.is_dirty || (r.is_added && r.policy_change.trim() !== '')
  ) || pendingDeleteIds.length > 0;

  if (fetchStatus === 'idle' || fetchStatus === 'loading') {
    return (
      <div className="mt-2">
        <CustomLoadingSpinner
          title="Loading Policy Improvements..."
          subtitle={RETRIEVING_DATA_SUBTITLE}
          minHeight="min-h-[100px]"
        />
      </div>
    );
  }

  if (fetchStatus === 'failed') {
    return (
      <ErrorDisplay
        title="Failed to load policy improvements"
        error={fetchError}
        onRetry={() => dispatch(fetchPolicyImprovementsThunk({ caseId }))}
      />
    );
  }

  const handleClosureNoteClick = (rowIndex: number) => {
    const row = dataItems[rowIndex];
    const hasNote = row?.comments && row.comments.trim().length > 0;

    setSelectedRowIndex(rowIndex);
    setModalVariant(hasNote ? 'view' : 'edit');
    setIsClosureNoteModalOpen(true);
  };

  const handleSaveClosureNote = (note: string) => {
    if (selectedRowIndex === null) return;
    handleTableChange(selectedRowIndex, note, 'comments');
  };

  const handleCloseClosureNoteModal = () => {
    setIsClosureNoteModalOpen(false);
    setSelectedRowIndex(null);
  };

  const handleAddRow = () => {
    if (hasEmptyNewRow) return;

    setLocalItems((prev) => [
      ...(prev ?? cloneFromSaved()),
      {
        policy_id: crypto.randomUUID(),
        policy_change: '',
        approver: '',
        implementation_date: null,
        comments: '',
        ai_generated: false,
        user_updated: false,
        last_updated_at: new Date().toISOString(),
        is_added: true,
        is_dirty: false,
      },
    ]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const row = dataItems[rowIndex];
    if (!row) return;

    if (row.is_added) {
      setLocalItems((prev) => prev!.filter((_, i) => i !== rowIndex));
      return;
    }

    setPendingDeleteIds((prev) => [...prev, row.policy_id]);
    setLocalItems((prev) => {
      const current = prev ?? cloneFromSaved();
      return current.filter((_, i) => i !== rowIndex);
    });
  };

  const handleTableChange = <T extends keyof PolicyImprovementItemUI>(
    rowIndex: number,
    value: PolicyImprovementItemUI[T],
    field: T
  ) => {
    if (isSaving) return;

    setLocalItems((prev) => {
      const current = prev ?? cloneFromSaved();
      const updated = [...current];
      updated[rowIndex] = {
        ...updated[rowIndex],
        [field]: value,
      };

      if (!updated[rowIndex].is_added) {
        const savedVersion = savedItems.find((s) => s.policy_id === updated[rowIndex].policy_id);
        if (savedVersion) {
          const { is_added: _a, is_dirty: _d, ...currentFields } = updated[rowIndex];
          const { is_added: _a2, is_dirty: _d2, ...savedFields } = savedVersion;
          const normalize = (v: unknown) => (v === null || v === undefined || v === '' ? '' : v);
          const isStillDirty = Object.keys(savedFields).some(
              (k) => normalize((currentFields as Record<string, unknown>)[k]) !== normalize((savedFields as Record<string, unknown>)[k])
          );
          updated[rowIndex] = { ...updated[rowIndex], is_dirty: isStillDirty };
        } else {
          updated[rowIndex] = { ...updated[rowIndex], is_dirty: true };
        }
      }

      return updated;
    });
  };

  const handleDiscardChanges = () => {
    setLocalItems(null);
    setPendingDeleteIds([]);
    setIsClosureNoteModalOpen(false);
    setSelectedRowIndex(null);
    setTableKey((prev) => prev + 1);
  };

  const handleSave = async () => {
    if (!isDirty) return;

    try {
      setIsSaving(true);

      const hasUpsertChanges = dataItems.some(
        (r) => r.is_dirty || (r.is_added && r.policy_change.trim() !== '')
      );

      if (hasUpsertChanges) {
        const policyImprovements = dataItems
          .filter((row) => row.policy_change.trim().length > 0)
          .map(({ is_added, is_dirty, ...rest }) => ({
              ...rest,
              user_updated: true,
          }));

        await dispatch(
          upsertPolicyImprovementsThunk({
            caseId,
            request: { policy_improvements: policyImprovements },
          })
        ).unwrap();
      }

      if (pendingDeleteIds.length > 0) {
        await Promise.all(
          pendingDeleteIds.map((policyId) =>
            dispatch(deletePolicyImprovementThunk({ caseId, policyId })).unwrap()
          )
        );
      }

      setLocalItems(null);
      setPendingDeleteIds([]);
      showSuccessToast('Policy improvements saved successfully!');
    } catch (error) {
      showErrorToast('Failed to save policy improvements');
      console.error('Save error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const columnConfigs: IColumnConfig<PolicyImprovementItemUI>[] = [
    {
      headerName: 'Suggested Policy Change',
      field: 'policy_change',
      type: 'custom',
      getValue: (data) => data.policy_change,
      render: (rowIndex, value) => (
        <EditableExpandableTextCell
          value={value as string}
          onChange={(val) =>
            handleTableChange(rowIndex, val, 'policy_change')
          }
        />
      ),
    },
    {
      headerName: 'Approved By',
      field: 'approver',
      type: 'text' as ColumnType,
      getValue: (data) => data.approver,
      isEditable: true,
      onChange: handleTableChange,
      textCellProps: {
        numberOfRows: 1,
        className: 'px-0 py-0 items-start min-w-[150px]',
        containerClassName: 'px-0 py-0 min-w-[150px]',
        textareaClassName: 'px-0',
      },
    },
    {
      headerName: 'Implementation Date',
      field: 'implementation_date',
      type: 'date' as ColumnType,
      getValue: (data) => data.implementation_date,
      isEditable: true,
      onChange: handleTableChange,
      dateCellProps: {
        className: 'px-0 py-0',
        inputClassName: 'px-0 py-0 bg-transparent min-w-[120px]',
      },
    },
    {
      headerName: 'Closure Note',
      field: 'comments',
      type: 'textbutton' as ColumnType,
      getValue: (data) => {
        const hasNote = data?.comments && data.comments.trim().length > 0;
        return hasNote ? 'View notes' : '+ Add closure note';
      },
      onClick: handleClosureNoteClick,
      textButtonCellProps: {
        className: 'px-0 min-w-[120px]',
        buttonClassName: 'px-0',
      },
    },
  ];

  const dataRows: IDataRow<PolicyImprovementItemUI>[] = dataItems.map((item) => ({
    data: item,
    isEditable: true,
    widgets: isSaving ? [] : (['delete', 'add'] as ('delete' | 'add')[]),
    onDelete: handleDeleteRow,
    onAdd: handleAddRow,
  }));

  const renderDefaultTableBody = () => (
    <div className="flex flex-col items-center justify-center py-8 px-4">
      <NoDataIcon />
      <span className="text-[#0A0A0A] font-sans font-semibold text-base leading-[1.5rem] text-center mt-4 mb-2 max-w-full">
        No policy improvements yet
      </span>
      <span className="text-[#737373] font-sans font-normal text-sm leading-[1.25rem] text-center mb-4 max-w-md">
        Suggest improvements to strengthen processes and policies and share with
        the right owners
      </span>
      <ButtonV2 onClick={handleAddRow}>
        <Plus className="w-4 h-4 mr-1" /> Add
      </ButtonV2>
    </div>
  );

  const renderDiscardChangesButton = () => (
    <ButtonV2
      variant="outline"
      onClick={handleDiscardChanges}
      disabled={isSaving}
      className={cn(
        'font-sans font-medium text-sm leading-5',
        isSaving && 'opacity-50 cursor-not-allowed'
      )}
      style={{
        width: '137.5px',
        height: '36px',
        borderColor: '#3551F3',
        color: '#3551F3',
        gap: '8px',
      }}
    >
      Discard Changes
    </ButtonV2>
  );

  const renderSaveButton = () => {
    const isDisabled = isSaving || !isDirty || hasEmptyNewRow;

    return (
      <ButtonV2
        variant="primary"
        onClick={handleSave}
        disabled={isDisabled}
        className={cn(
          'font-sans font-medium text-sm leading-5 flex items-center justify-center w-[137.5px] h-[36px] gap-2',
          isDisabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <Save className="w-4 h-4 relative right-[4px]" />
        <span className="font-sans font-medium text-sm leading-5 align-middle">
          {isSaving ? 'Saving...' : 'Save'}
        </span>
      </ButtonV2>
    );
  };

  const renderFooter = () => (
    <div className="w-full min-h-[44px] flex justify-end items-center gap-5 rounded-lg py-2 px-2.5 border bg-[#F1F5F9] mt-4">
      <div className="flex flex-col items-end flex-1">
        <span className="font-sans font-semibold text-sm leading-5 tracking-normal text-[#737373]">
          {hasEmptyNewRow
            ? 'Add a suggested policy change to enable Save'
            : isDirty
              ? 'You have made changes, save now'
              : ''}
        </span>
      </div>
      {renderDiscardChangesButton()}
      {renderSaveButton()}
    </div>
  );

  return (
      <div className="w-full mb-6">
        <BaseDataTable
          key={tableKey}
          columnConfigs={columnConfigs}
          dataRows={dataRows}
          showDefaultTableBody={dataItems.length === 0}
          defaultTableBody={renderDefaultTableBody()}
        />
        {(isDirty || hasEmptyNewRow || pendingDeleteIds.length > 0) && renderFooter()}
        {isClosureNoteModalOpen && (
          <ClosureNoteModal
            isOpen={isClosureNoteModalOpen}
            onClose={handleCloseClosureNoteModal}
            variant={modalVariant}
            initialNote={
              selectedRowIndex !== null
                ? dataItems[selectedRowIndex]?.comments || ''
                : ''
            }
            onSave={handleSaveClosureNote}
          />
        )}
      </div>
  );
};

export default PolicyImprovementsTableV2;